import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Product, Batch, BatchDetail, Material, SubGroup, PackagingOrder } from './mockData';
import { FilterState } from './FilterBar';
import { ChevronRight, ChevronLeft, Settings, UtensilsCrossed, Cloud, Scissors, Package, Info, X } from 'lucide-react';
interface ChargeDetailProps {
  product: Product;
  batch: Batch;
  details: BatchDetail | null;
  filters: FilterState;
  visibleColumns: string[];
  onBackToProduct: () => void;
  onBackToBatchList: () => void;
  returnToTransfer?: boolean;
  onBackToTransfer?: () => void;
}
const iconMap = {
  Settings: Settings,
  UtensilsCrossed: UtensilsCrossed,
  Cloud: Cloud,
  Scissors: Scissors,
  Package: Package
};
const ALL_COLUMNS: {
  key: string;
  label: string;
  align?: 'right';
}[] = [{
  key: 'name',
  label: 'Material'
}, {
  key: 'batchNo',
  label: 'Chargen-Nr.'
}, {
  key: 'supplier',
  label: 'Lieferant'
}, {
  key: 'scan',
  label: 'Scan'
}, {
  key: 'mhd',
  label: 'MHD'
}, {
  key: 'we',
  label: 'WE'
}, {
  key: 'quantity',
  label: 'Menge',
  align: 'right'
}];
// Per-section default hidden columns. These are removed from the user's visible-columns
// set on a per-section basis (Spalten-Toggle bleibt global, dient als Obergrenze).
const SECTION_HIDDEN_COLS: Record<string, string[]> = {
  Füllerei: ['we', 'quantity'],
  Räucherei: ['mhd', 'we', 'quantity']
};
// Shared column widths so columns line up vertically across every section table.
// Pixel-based; tables use table-layout: fixed and only render the columns they need —
// but each rendered column always claims the same width as elsewhere on the page.
const COL_PX_WIDTH: Record<string, number> = {
  name: 280,
  batchNo: 200,
  supplier: 200,
  scan: 240,
  mhd: 140,
  we: 130,
  quantity: 120,
  // Verpackung-specific
  action: 130
};
function colsForSection<T extends {
  key: string;
}>(sectionTitle: string, cols: T[]): T[] {
  const hidden = SECTION_HIDDEN_COLS[sectionTitle];
  if (!hidden || hidden.length === 0) return cols;
  return cols.filter(c => !hidden.includes(c.key));
}
function matchesSearch(m: Material, filters: FilterState): boolean {
  const term = filters.search.trim().toLowerCase();
  if (!term) return true;
  return filters.searchFields.some(fieldKey => {
    if (fieldKey === 'Material') return m.name.toLowerCase().includes(term);
    if (fieldKey === 'Lieferant') return m.supplier.toLowerCase().includes(term);
    if (fieldKey === 'Chargen-Nr.') return m.batchNo.toLowerCase().includes(term);
    if (fieldKey === 'MHD') return m.mhd.toLowerCase().includes(term);
    return false;
  });
}
export function ChargeDetail({
  product,
  batch,
  details,
  filters,
  visibleColumns,
  onBackToProduct,
  onBackToBatchList,
  returnToTransfer,
  onBackToTransfer
}: ChargeDetailProps) {
  if (!details) {
    return <div className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 py-12 text-center text-gray-500" data-id="element-66">
        Keine Detaildaten für diese Charge verfügbar.
      </div>;
  }
  const cols = ALL_COLUMNS.filter(c => visibleColumns.includes(c.key));
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 pb-12" data-id="element-67">
      {/* Breadcrumb */}
      <nav className="flex items-center text-sm text-gray-500 mb-6 flex-wrap gap-y-2" data-id="element-68">
        {returnToTransfer ? <>
            <button onClick={onBackToTransfer} className="hover:text-[#E87722] flex items-center transition-colors" data-id="element-69">
              <ChevronLeft className="w-4 h-4 mr-1" data-id="element-70" />
              Chargen übertragen
            </button>
            <ChevronRight className="w-4 h-4 mx-2 text-gray-400" data-id="element-71" />
            <span className="font-medium text-gray-900" data-id="element-72">
              {batch.batchNumber}
            </span>
          </> : <>
            <button onClick={onBackToProduct} className="hover:text-[#E87722] flex items-center transition-colors" data-id="element-73">
              <ChevronLeft className="w-4 h-4 mr-1" data-id="element-74" />
              Produkte
            </button>
            <ChevronRight className="w-4 h-4 mx-2 text-gray-400" data-id="element-75" />
            <button onClick={onBackToBatchList} className="hover:text-[#E87722] transition-colors" data-id="element-76">
              {product.name}
            </button>
            <ChevronRight className="w-4 h-4 mx-2 text-gray-400" data-id="element-77" />
            <span className="font-medium text-gray-900" data-id="element-78">
              {batch.batchNumber}
            </span>
          </>}
      </nav>

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-8" data-id="element-79">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-80">
          <div data-id="element-81">
            <h1 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-82">
              Charge {batch.batchNumber}
            </h1>
            <p className="text-gray-500" data-id="element-83">
              {product.name} • Produziert am {batch.productionDate}
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-84">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${batch.status === 'Abgeschlossen' ? 'bg-green-100 text-green-800' : batch.status === 'Bereit zum Verpacken' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-85">
              {batch.status}
            </span>
            <button className="px-4 py-2 bg-[#E87722] text-white rounded-lg text-sm font-medium hover:bg-[#d0681b] transition-colors shadow-sm" data-id="element-86">
              Bericht exportieren
            </button>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-6" data-id="element-87">
        {details.sections.map((section, index) => {
        const Icon = iconMap[section.iconName];
        return <motion.div key={section.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.05
        }} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-88">
              {/* Section Header */}
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50 flex items-center gap-3" data-id="element-89">
                <div className="p-2 bg-[#16A34A]/10 rounded-full" data-id="element-90">
                  <Icon className="w-5 h-5 text-[#16A34A]" data-id="element-91" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-92">
                  {section.title}
                </h2>
                {section.subGroups && section.subGroups.length > 0 && <span className="ml-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full font-medium" data-id="element-93">
                    {section.subGroups.length}{' '}
                    {section.title === 'Räucherei' ? 'Kammern' : 'Paletten'}
                  </span>}
                {section.packagingOrders && section.packagingOrders.length > 0 && <span className="ml-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full font-medium" data-id="element-94">
                      {section.packagingOrders.length} Aufträge
                    </span>}
              </div>

              {/* Body: packagingOrders | Schälerei palette rows | subGroups tabs | flat */}
              {section.packagingOrders && section.packagingOrders.length > 0 ? <PackagingTable orders={section.packagingOrders} data-id="element-95" /> : section.title === 'Schälerei' && section.subGroups && section.subGroups.length > 0 ? <PaletteTable palettes={section.subGroups} data-id="element-96" /> : section.subGroups && section.subGroups.length > 0 ? <SubGroupTabs subGroups={section.subGroups} cols={colsForSection(section.title, cols)} filters={filters} sectionTitle={section.title} data-id="element-97" /> : <MaterialsTable materials={section.materials.filter(m => matchesSearch(m, filters))} cols={colsForSection(section.title, cols)} data-id="element-98" />}
            </motion.div>;
      })}
      </div>
    </motion.div>;
}
// ---------- SubGroupTabs ----------
function SubGroupTabs({
  subGroups,
  cols,
  filters,
  sectionTitle
}: {
  subGroups: SubGroup[];
  cols: {
    key: string;
    label: string;
    align?: 'right';
  }[];
  filters: FilterState;
  sectionTitle: string;
}) {
  const [activeId, setActiveId] = useState(subGroups[0]?.id);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  // Reset active when subGroups change
  useEffect(() => {
    if (!subGroups.find(g => g.id === activeId)) {
      setActiveId(subGroups[0]?.id);
    }
  }, [subGroups, activeId]);
  // Track scroll state for the chevron buttons
  const updateScrollState = () => {
    const el = tabsRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };
  useEffect(() => {
    updateScrollState();
    const el = tabsRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [subGroups.length]);
  const scrollBy = (dir: 1 | -1) => {
    const el = tabsRef.current;
    if (!el) return;
    el.scrollBy({
      left: dir * 200,
      behavior: 'smooth'
    });
  };
  const active = subGroups.find(g => g.id === activeId) ?? subGroups[0];
  const filteredMaterials = active.materials.filter(m => matchesSearch(m, filters));
  // Count matches per tab to surface filter hits
  const matchCounts = useMemo(() => Object.fromEntries(subGroups.map(g => [g.id, g.materials.filter(m => matchesSearch(m, filters)).length])), [subGroups, filters]);
  const term = filters.search.trim();
  return <div data-id="element-99">
      {/* Tab bar with scroll affordance */}
      <div className="relative border-b border-gray-200 bg-white" data-id="element-100">
        {canScrollLeft && <button onClick={() => scrollBy(-1)} className="absolute left-0 top-0 bottom-0 z-10 px-1.5 bg-gradient-to-r from-white via-white to-transparent flex items-center" aria-label="Nach links scrollen" data-id="element-101">
            <ChevronLeft className="w-4 h-4 text-gray-500" data-id="element-102" />
          </button>}
        {canScrollRight && <button onClick={() => scrollBy(1)} className="absolute right-0 top-0 bottom-0 z-10 px-1.5 bg-gradient-to-l from-white via-white to-transparent flex items-center" aria-label="Nach rechts scrollen" data-id="element-103">
            <ChevronRight className="w-4 h-4 text-gray-500" data-id="element-104" />
          </button>}

        <div ref={tabsRef} className="flex overflow-x-auto scrollbar-thin scroll-smooth px-2" style={{
        scrollbarWidth: 'thin'
      }} data-id="element-105">
          {subGroups.map(g => {
          const isActive = g.id === activeId;
          const count = matchCounts[g.id] ?? 0;
          const filteredOut = term && count === 0;
          return <button key={g.id} onClick={() => setActiveId(g.id)} className={`relative whitespace-nowrap px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${isActive ? 'text-[#E87722]' : filteredOut ? 'text-gray-300 hover:text-gray-500' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-106">
                {g.label}
                <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-semibold ${isActive ? 'bg-[#E87722] text-white' : term && count > 0 ? 'bg-[#E87722]/15 text-[#E87722]' : 'bg-gray-100 text-gray-500'}`} data-id="element-107">
                  {term ? count : g.materials.length}
                </span>
                {isActive && <motion.div layoutId={`tab-underline-${sectionTitle}`} className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#E87722] rounded-t" data-id="element-108" />}
              </button>;
        })}
        </div>
      </div>

      {/* Active sub-group table */}
      <AnimatePresence mode="wait" data-id="element-109">
        <motion.div key={active.id} initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.15
      }} data-id="element-110">
          <MaterialsTable materials={filteredMaterials} cols={cols} data-id="element-111" />
        </motion.div>
      </AnimatePresence>
    </div>;
}
// ---------- PackagingTable ----------
function PackagingTable({
  orders
}: {
  orders: PackagingOrder[];
}) {
  const [selectedOrder, setSelectedOrder] = useState<PackagingOrder | null>(null);
  return <>
      <div className="overflow-x-auto" data-id="element-112">
        <table className="w-full divide-y divide-gray-200 table-fixed" data-id="element-113">
          <colgroup data-id="element-114">
            <col style={{
            width: COL_PX_WIDTH.name
          }} data-id="element-115" />
            <col style={{
            width: COL_PX_WIDTH.batchNo
          }} data-id="element-116" />
            <col style={{
            width: COL_PX_WIDTH.supplier
          }} data-id="element-117" />
            <col style={{
            width: COL_PX_WIDTH.scan
          }} data-id="element-118" />
            <col style={{
            width: COL_PX_WIDTH.action
          }} data-id="element-119" />
            <col data-id="element-120" />
          </colgroup>
          <thead className="bg-white" data-id="element-121">
            <tr data-id="element-122">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-123">
                Kunde
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-124">
                Bestellnummer
              </th>
              <th data-id="element-125"></th>
              <th data-id="element-126"></th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-127">
                <span className="sr-only" data-id="element-128">Details</span>
              </th>
              <th data-id="element-129"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100" data-id="element-130">
            {orders.map(order => <tr key={order.id} className="hover:bg-gray-50 transition-colors" data-id="element-131">
                <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-132">
                  {order.customer}
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600 font-mono" data-id="element-133">
                  {order.orderNumber}
                </td>
                <td data-id="element-134"></td>
                <td data-id="element-135"></td>
                <td className="px-6 py-3 whitespace-nowrap text-right" data-id="element-136">
                  <button onClick={() => setSelectedOrder(order)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-[#E87722] hover:bg-[#E87722]/10 transition-colors" data-id="element-137">
                    <Info className="w-3.5 h-3.5" data-id="element-138" />
                    Details
                  </button>
                </td>
                <td data-id="element-139"></td>
              </tr>)}
          </tbody>
        </table>
      </div>

      <AnimatePresence data-id="element-140">
        {selectedOrder && <PackagingDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} data-id="element-141" />}
      </AnimatePresence>
    </>;
}
type OrderTab = 'chargen' | 'rohstoffe' | 'produkte';
function PackagingDetailModal({
  order,
  onClose
}: {
  order: PackagingOrder;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<OrderTab>('chargen');
  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const tabs: {
    id: OrderTab;
    label: string;
    linkLabel: string;
    items: typeof order.chargen;
  }[] = [{
    id: 'chargen',
    label: 'CHARGEN',
    linkLabel: 'Zur Charge',
    items: order.chargen ?? []
  }, {
    id: 'rohstoffe',
    label: 'ROHSTOFFE',
    linkLabel: 'Zum Rohstoff',
    items: order.rohstoffe ?? []
  }, {
    id: 'produkte',
    label: 'PRODUKTE',
    linkLabel: 'Zum Produkt',
    items: order.produkte ?? []
  }];
  const currentTab = tabs.find(t => t.id === tab) ?? tabs[0];
  const numberColLabel = tab === 'chargen' ? 'Chargen-Nr.' : tab === 'rohstoffe' ? 'Rohstoff-Nr.' : 'Produkt-Nr.';
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.15
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose} data-id="element-142">
      <motion.div initial={{
      scale: 0.96,
      opacity: 0,
      y: 12
    }} animate={{
      scale: 1,
      opacity: 1,
      y: 0
    }} exit={{
      scale: 0.96,
      opacity: 0,
      y: 12
    }} transition={{
      duration: 0.18
    }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden" data-id="element-143">
        {/* Header */}
        <div className="px-6 py-5 flex items-start justify-between gap-4" data-id="element-144">
          <h3 className="text-2xl font-semibold text-gray-900" data-id="element-145">
            Bestellung: {order.orderNumber}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition-colors -mt-1 -mr-1" aria-label="Schließen" data-id="element-146">
            <X className="w-5 h-5" data-id="element-147" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-gray-200" data-id="element-148">
          <div className="flex gap-6" data-id="element-149">
            {tabs.map(t => {
            const isActive = t.id === tab;
            return <button key={t.id} onClick={() => setTab(t.id)} className={`relative py-3 text-sm font-semibold tracking-wider transition-colors ${isActive ? 'text-[#E87722]' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-150">
                  {t.label}
                  {isActive && <motion.div layoutId="order-tab-underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#E87722] rounded-t" data-id="element-151" />}
                </button>;
          })}
          </div>
        </div>

        {/* Tab content */}
        <div className="p-6" data-id="element-152">
          <div className="rounded-lg border border-gray-200 overflow-hidden" data-id="element-153">
            <div className="grid grid-cols-[1fr_1fr_auto] gap-4 px-5 py-3 bg-gray-50 border-b border-gray-200 text-sm font-semibold text-gray-700" data-id="element-154">
              <div data-id="element-155">{numberColLabel}</div>
              <div data-id="element-156">Scan</div>
              <div className="w-20" data-id="element-157" />
            </div>

            <AnimatePresence mode="wait" data-id="element-158">
              <motion.div key={tab} initial={{
              opacity: 0,
              y: 4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} transition={{
              duration: 0.12
            }} className="divide-y divide-gray-100" data-id="element-159">
                {currentTab.items && currentTab.items.length > 0 ? currentTab.items.map(item => <div key={item.id} className="grid grid-cols-[1fr_1fr_auto] gap-4 px-5 py-3.5 items-center hover:bg-gray-50 transition-colors" data-id="element-160">
                      <div className="text-sm text-gray-900 font-mono" data-id="element-161">
                        {item.number}
                      </div>
                      <div className="text-sm text-gray-600 tabular-nums" data-id="element-162">
                        {item.scan}
                      </div>
                      <button className="text-sm text-[#E87722] hover:text-[#d0681b] underline underline-offset-2 font-medium whitespace-nowrap" data-id="element-163">
                        {currentTab.linkLabel}
                      </button>
                    </div>) : <div className="px-5 py-8 text-center text-sm text-gray-400" data-id="element-164">
                    Keine Einträge.
                  </div>}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
// ---------- PaletteTable (Schälerei: flat palette rows) ----------
function PaletteTable({
  palettes
}: {
  palettes: SubGroup[];
}) {
  return <div className="overflow-x-auto" data-id="element-165">
      <table className="w-full divide-y divide-gray-200 table-fixed" data-id="element-166">
        <colgroup data-id="element-167">
          <col style={{
          width: COL_PX_WIDTH.name
        }} data-id="element-168" />
          <col style={{
          width: COL_PX_WIDTH.batchNo
        }} data-id="element-169" />
          <col style={{
          width: COL_PX_WIDTH.supplier
        }} data-id="element-170" />
          <col style={{
          width: COL_PX_WIDTH.scan
        }} data-id="element-171" />
          <col data-id="element-172" />
        </colgroup>
        <thead className="bg-white" data-id="element-173">
          <tr data-id="element-174">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-175">
              Paletten-Nr.
            </th>
            <th data-id="element-176"></th>
            <th data-id="element-177"></th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-178">
              Scan
            </th>
            <th data-id="element-179"></th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100" data-id="element-180">
          {palettes.map((palette, i) => <motion.tr key={palette.id} initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.06,
          duration: 0.25,
          ease: 'easeOut'
        }} className="hover:bg-gray-50 transition-colors" data-id="element-181">
              <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900 font-mono" data-id="element-182">
                {palette.label}
              </td>
              <td data-id="element-183"></td>
              <td data-id="element-184"></td>
              <td className="px-6 py-3 whitespace-nowrap text-sm" data-id="element-185">
                {palette.scan && palette.scan !== '-' ? <div className="flex items-center gap-2" data-id="element-186">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-800" data-id="element-187">
                      OK
                    </span>
                    <span className="text-xs text-gray-600 tabular-nums whitespace-nowrap" data-id="element-188">
                      {palette.scan}
                    </span>
                  </div> : <span className="text-gray-400" data-id="element-189">–</span>}
              </td>
              <td data-id="element-190"></td>
            </motion.tr>)}
        </tbody>
      </table>
    </div>;
}
// ---------- MaterialsTable ----------
function MaterialsTable({
  materials,
  cols
}: {
  materials: Material[];
  cols: {
    key: string;
    label: string;
    align?: 'right';
  }[];
}) {
  return <div className="overflow-x-auto" data-id="element-191">
      <table className="w-full divide-y divide-gray-200 table-fixed" data-id="element-192">
        <colgroup data-id="element-193">
          {cols.map(c => <col key={c.key} style={{
          width: COL_PX_WIDTH[c.key] ?? 160
        }} data-id="element-194" />)}
          <col data-id="element-195" />
        </colgroup>
        <thead className="bg-white" data-id="element-196">
          <tr data-id="element-197">
            {cols.map(c => <th key={c.key} className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${c.align === 'right' ? 'text-right' : 'text-left'}`} data-id="element-198">
                {c.label}
              </th>)}
            <th data-id="element-199"></th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100" data-id="element-200">
          {materials.map(mat => <tr key={mat.id} className="hover:bg-gray-50 transition-colors" data-id="element-201">
              {cols.map(c => <td key={c.key} className={`px-6 py-3 whitespace-nowrap text-sm ${c.align === 'right' ? 'text-right font-medium text-gray-900' : ''} ${c.key === 'name' ? 'font-medium text-gray-900' : 'text-gray-600'} ${c.key === 'batchNo' ? 'font-mono' : ''}`} data-id="element-202">
                  {c.key === 'scan' ? mat.scan && mat.scan !== '-' ? <div className="flex items-center gap-2" data-id="element-203">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-800" data-id="element-204">
                          OK
                        </span>
                        <span className="text-xs text-gray-600 tabular-nums whitespace-nowrap" data-id="element-205">
                          {mat.scan}
                        </span>
                      </div> : <span className="text-gray-400" data-id="element-206">{mat.scan}</span> : (mat as any)[c.key]}
                </td>)}
              <td data-id="element-207"></td>
            </tr>)}
          {materials.length === 0 && <tr data-id="element-208">
              <td colSpan={cols.length + 1} className="px-6 py-6 text-center text-sm text-gray-400" data-id="element-209">
                Keine Einträge entsprechen den Filtern.
              </td>
            </tr>}
        </tbody>
      </table>
    </div>;
}